import type { FC } from 'react';
import { Fragment } from 'react';
import type { AuctionDetail, Price } from 'src/apis/monolith/types';
import { DataItems } from 'src/components/data-display/data-list';
import { FormatValue } from 'src/components/data-display/format-value';
import { Spacer } from 'src/components/layout/spacer';
import {
  isLongTermAuction,
  isShortTermAuction,
} from 'src/pages/reporting/auctions/details/utils';
import {
  type FeesInEURMWh,
  type PricesInEURMWh,
} from 'src/pages/transport/auctions/details/helper';

type AuctionFees = {
  auction: AuctionDetail;
  // null means that prices are either not available in EUR/MWh (e.g. missing exchange rates)
  // or that they haven't been selected by the user as the preferred unit
  pricesInEURMWh: PricesInEURMWh;
};

export const AuctionFees: FC<AuctionFees> = ({ auction, pricesInEURMWh }) => {
  return (
    <>
      <Fees
        auction={auction}
        regulatedTariff={auction.regulatedTariff0}
        accountingCharge={auction.accountingCharge0}
        gasQualityConversionCharge={auction.gasQualityConversionCharge0}
        measurementCharge={auction.measurementCharge0}
        biogasCharge={auction.biogasCharge0}
        tsoName={auction.networkPoint.tso0Name}
        volumeTariffAvailable={auction.volumeTariffAvailable0}
        operatingCharge={auction.operatingCharge0}
        balancingCharge={auction.balancingCharge0}
        commodityFee={auction.commodityFee0}
        tsoIdForTestId={auction.networkPoint.tso0Id}
        feesInEURMWh={pricesInEURMWh?.fees0}
      />

      {auction.networkPoint.bundled && (
        <Fees
          auction={auction}
          tsoName={auction.networkPoint.tso1Name}
          regulatedTariff={auction.regulatedTariff1}
          accountingCharge={auction.accountingCharge1}
          gasQualityConversionCharge={auction.gasQualityConversionCharge1}
          measurementCharge={auction.measurementCharge1}
          biogasCharge={auction.biogasCharge1}
          volumeTariffAvailable={auction.volumeTariffAvailable1}
          operatingCharge={auction.operatingCharge1}
          balancingCharge={auction.balancingCharge1}
          commodityFee={auction.commodityFee1}
          tsoIdForTestId={auction.networkPoint.tso1Id}
          feesInEURMWh={pricesInEURMWh?.fees1}
        />
      )}
    </>
  );
};

type FeesProps = {
  tsoName: string;
  auction: AuctionDetail;
  volumeTariffAvailable?: boolean;
  regulatedTariff?: Price;
  operatingCharge?: Price;
  accountingCharge?: Price;
  gasQualityConversionCharge?: Price;
  measurementCharge?: Price;
  biogasCharge?: Price;
  balancingCharge?: Price;
  commodityFee?: Price;
  tsoIdForTestId: number;
  feesInEURMWh?: FeesInEURMWh;
};

const Fees: FC<FeesProps> = ({
  auction,
  tsoName,
  volumeTariffAvailable,
  regulatedTariff,
  operatingCharge,
  accountingCharge,
  balancingCharge,
  commodityFee,
  biogasCharge,
  gasQualityConversionCharge,
  measurementCharge,
  tsoIdForTestId,
  feesInEURMWh,
}) => {
  return (
    <>
      <Spacer />
      <strong>Fees of {tsoName}</strong>
      <DataItems>
        {volumeTariffAvailable && isLongTermAuction(auction) && (
          <>
            {auction.yearlyVolumeTariffs.map((yvt, idx) => (
              <Fragment key={`${auction.id}-${idx}`}>
                <strong>{yvt.year}</strong>
                <span>Volume Tariffs are no longer supported!</span>
              </Fragment>
            ))}
          </>
        )}

        {volumeTariffAvailable && isShortTermAuction(auction) && (
          <span>Volume Tariffs are no longer supported!</span>
        )}

        {!volumeTariffAvailable && regulatedTariff && (
          <>
            <span>Regulated Capacity Tariff</span>
            <span>
              {feesInEURMWh?.regulatedTariff || (
                <FormatValue
                  value={regulatedTariff.value.value}
                  label={regulatedTariff.unit.label}
                />
              )}
            </span>
          </>
        )}

        {operatingCharge && (
          <>
            <span>Measuring Operations Fee</span>
            <span>
              {feesInEURMWh?.operatingCharge || (
                <FormatValue
                  value={operatingCharge.value.value}
                  label={operatingCharge.unit.label}
                />
              )}
            </span>
          </>
        )}

        {accountingCharge && (
          <>
            <span>Billing Fee</span>
            <span>
              {feesInEURMWh?.accountingCharge || (
                <FormatValue
                  value={accountingCharge.value.value}
                  label={accountingCharge.unit.label}
                />
              )}
            </span>
          </>
        )}

        {gasQualityConversionCharge && (
          <>
            <span>Gas Quality Conversion Fee</span>
            <span>
              {feesInEURMWh?.gasQualityConversionCharge || (
                <FormatValue
                  value={gasQualityConversionCharge.value.value}
                  label={gasQualityConversionCharge.unit.label}
                />
              )}
            </span>
          </>
        )}

        {measurementCharge && (
          <>
            <span>Measuring Fee</span>
            <span data-testid={`tso-${tsoIdForTestId}-fee-measuring`}>
              {feesInEURMWh?.measurementCharge || (
                <FormatValue
                  value={measurementCharge.value.value}
                  label={measurementCharge.unit.label}
                />
              )}
            </span>
          </>
        )}

        {biogasCharge && (
          <>
            <span>Biogas Redistribution Levy</span>
            <span>
              {feesInEURMWh?.biogasCharge || (
                <FormatValue
                  value={biogasCharge.value.value}
                  label={biogasCharge.unit.label}
                />
              )}
            </span>
          </>
        )}

        {balancingCharge && (
          <>
            <span>Neutrality Charge</span>
            <span>
              {feesInEURMWh?.balancingCharge || (
                <FormatValue
                  value={balancingCharge.value.value}
                  label={balancingCharge.unit.label}
                />
              )}
            </span>
          </>
        )}

        {commodityFee && (
          <>
            <span>Commodity Fee</span>
            <span>
              {feesInEURMWh?.commodityFee || (
                <FormatValue
                  value={commodityFee.value.value}
                  label={commodityFee.unit.label}
                />
              )}
            </span>
          </>
        )}
      </DataItems>
    </>
  );
};
